import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Camera, ImageIcon, CheckSquare, Sparkles } from "lucide-react";
import { PhotoTile } from "@/components/photos/PhotoTile";
import { PhotoFilters } from "@/components/photos/PhotoFilters";
import { CategoryManager } from "@/components/photos/CategoryManager";
import { catOf, usePhotoCategories } from "@/components/photos/usePhotoCategories";
import { PhotoViewer } from "@/components/photos/PhotoViewer";
import { SelectionBar } from "@/components/photos/SelectionBar";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageUpload";
import { UploadingThumb } from "@/components/UploadingThumb";
import { useFileDrop } from "@/lib/useFileDrop";

export function JobPhotos({ jobId, visitId, onChanged, onlineReads = false }) {
  const { user } = useAuth();
  const isOffice = user.role !== "field";
  const canManage = ["admin", "office", "super_admin"].includes(user.role);
  const { categories, setCategories } = usePhotoCategories();
  const [photos, setPhotos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [readError, setReadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]); // in-flight: {key,url,pct,error}
  const [viewing, setViewing] = useState(null); // index into the filtered list
  const [filterCat, setFilterCat] = useState("all");
  const [uploader, setUploader] = useState("all");
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(null);
  const shown = useMemo(() => photos.filter((p) => (filterCat === "all" || catOf(p, categories) === filterCat) && (uploader === "all" || p.uploaded_by_id === uploader)), [photos, categories, filterCat, uploader]);
  const canEdit = (p) => isOffice || p.uploaded_by_id === user.id;
  const inputRef = useRef(null);
  const { dragging, dropProps } = useFileDrop((f) => handleFiles(f), { disabled: uploading });

  const load = () => {
    if (onlineReads) { setLoaded(false); setReadError(false); }
    api.get(`/jobs/${jobId}/photos`, { params: visitId ? {visit_id:visitId} : undefined, __flSkipReadCache: onlineReads })
      .then((r) => setPhotos(r.data.items || []))
      .catch(() => { if (onlineReads) setReadError(true); })
      .finally(() => setLoaded(true));
  };
  useEffect(load, [jobId, visitId]);
  const sorting = photos.some((p) => p.ai_status === "pending");
  useEffect(() => {
    if (!sorting) return;
    const t = setTimeout(() => api.get(`/jobs/${jobId}/photos`, { params: visitId ? { visit_id: visitId } : undefined, __flSkipReadCache: true })
      .then((r) => setPhotos(r.data.items || [])).catch(() => {}), 3000);
    return () => clearTimeout(t);
  }, [sorting, photos, jobId, visitId]);

  const autoSort = async () => {
    setBusy("sort");
    try {
      const { data } = await api.post(`/jobs/${jobId}/photos/auto-sort`, {}, { __flOnlineOnly: true });
      toast.success(data.queued ? `Sorting ${data.queued} photo${data.queued > 1 ? "s" : ""} with AI…` : "Nothing to sort. Photos you've moved by hand are kept as they are");
      if (data.queued) setPhotos((arr) => arr.map((p) => (p.category_source === "user" ? p : { ...p, ai_status: "pending" })));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start sorting");
    } finally { setBusy(null); }
  };

  const bulkMove = async (cat) => {
    setBusy("move");
    try {
      const { data } = await api.post(`/jobs/${jobId}/photos/bulk-category`, { ids: [...selected], category: cat }, { __flOnlineOnly: true });
      const done = new Set(data.updated);
      setPhotos((arr) => arr.map((p) => (done.has(p.id) ? { ...p, category: data.category, category_source: "user" } : p)));
      toast.success(`${done.size} moved to ${data.category}${data.skipped ? ` · ${data.skipped} not yours to change` : ""}`);
      endSelect();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Move failed");
    } finally { setBusy(null); }
  };

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    if (inputRef.current) inputRef.current.value = "";
    handleFiles(files);
  };

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    const items = files.map((f, i) => ({ key: `${Date.now()}-${i}`, file: f, url: URL.createObjectURL(f), pct: 0 }));
    setUploads((u) => [...u, ...items]);
    let ok = 0;
    let idx = 0;
    const setPct = (key, pct) => setUploads((u) => u.map((x) => (x.key === key ? { ...x, pct } : x)));
    const drop = (key) => setUploads((u) => {
      const it = u.find((x) => x.key === key);
      if (it?.url) URL.revokeObjectURL(it.url);
      return u.filter((x) => x.key !== key);
    });
    const worker = async () => {
      while (idx < items.length) {
        const item = items[idx++];
        try {
          const small = await compressImage(item.file);
          const fd = new FormData();
          fd.append("file", small, small.name);
          if (visitId) fd.append("visit_id", visitId);
          const { data } = await api.post(`/jobs/${jobId}/photos`, fd, {
            __flOnlineOnly: !!visitId,
            headers: { "Content-Type": "multipart/form-data" },
            onUploadProgress: (ev) => { if (ev.total) setPct(item.key, Math.min(99, Math.round((ev.loaded / ev.total) * 100))); },
          });
          setPhotos((arr) => [data, ...arr]);
          drop(item.key);
          ok += 1;
        } catch (err) {
          toast.error(err?.response?.data?.detail || `Upload failed: ${item.file.name}`);
          setUploads((u) => u.map((x) => (x.key === item.key ? { ...x, error: true } : x)));
          setTimeout(() => drop(item.key), 4000);
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, items.length) }, worker));
    if (ok) toast.success(`${ok} photo${ok > 1 ? "s" : ""} uploaded`);
    setUploading(false);
    if(ok) onChanged?.();
  };

  const remove = async (p) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/jobs/${jobId}/photos/${p.id}`, {__flOnlineOnly: !!visitId});
      setPhotos((arr) => arr.filter((x) => x.id !== p.id));
      setViewing((v) => (v === null || shown.length <= 1 ? null : Math.min(v, shown.length - 2)));
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const save = async (p, changes) => {
    try {
      const { data } = await api.patch(`/jobs/${jobId}/photos/${p.id}`, changes, { __flOnlineOnly: true });
      setPhotos((arr) => arr.map((x) => (x.id === p.id ? { ...x, ...data } : x)));
      if (changes.caption !== undefined) toast.success("Caption saved");
      if (changes.category) toast.success(`Moved to ${data.category}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save");
    }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const endSelect = () => { setSelecting(false); setSelected(new Set()); };

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} photo${selected.size > 1 ? "s" : ""}?`)) return;
    setBusy("delete");
    try {
      const { data } = await api.post(`/jobs/${jobId}/photos/bulk-delete`, { ids: [...selected] }, { __flOnlineOnly: true });
      const gone = new Set(data.deleted);
      setPhotos((arr) => arr.filter((x) => !gone.has(x.id)));
      toast.success(`${gone.size} deleted${data.skipped ? ` · ${data.skipped} not yours to delete` : ""}`);
      endSelect();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally { setBusy(null); }
  };

  const bulkDownload = async () => {
    setBusy("zip");
    try {
      const { data, headers } = await api.post(`/jobs/${jobId}/photos/zip`, { ids: [...selected] }, { responseType: "blob", __flOnlineOnly: true });
      const url = URL.createObjectURL(data);
      const name = /filename="([^"]+)"/.exec(headers?.["content-disposition"] || "")?.[1] || "job-photos.zip";
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Download failed");
    } finally { setBusy(null); }
  };

  if (onlineReads && readError) return <p role="alert" className="p-4 text-sm">This section could not be loaded. <Button variant="outline" onClick={load}>Retry</Button></p>;
  if (!loaded) return onlineReads ? <p role="status" className="p-4 text-sm">Loading…</p> : null;

  return (
    <section className={`hard-card p-4 sm:p-5 transition-colors ${dragging ? "ring-2 ring-[#FF5F15] bg-orange-50/40" : ""}`} {...dropProps} data-testid="job-photos-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-[#FF5F15]" />
          <h3 className="font-semibold text-base">{dragging ? "Drop photos to upload" : "Site photos"}</h3>
          {photos.length > 0 && <span className="text-sm text-muted-foreground" data-testid="job-photos-count">{photos.length}</span>}
        </div>
        <div className="flex items-center gap-2">
          {photos.length > 0 && !selecting && <Button size="sm" variant="ghost" onClick={() => setSelecting(true)} className="h-9 rounded-full" data-testid="job-photo-select-mode"><CheckSquare className="w-4 h-4 mr-1.5" />Select</Button>}
          {isOffice && photos.length > 0 && !selecting && <Button size="sm" variant="ghost" onClick={autoSort} disabled={busy === "sort" || sorting} className="h-9 rounded-full" data-testid="job-photo-auto-sort"><Sparkles className="w-4 h-4 mr-1.5" />{sorting ? "Sorting…" : "Sort with AI"}</Button>}
          {canManage && !selecting && <CategoryManager categories={categories} onSaved={(c) => { setCategories(c); load(); }} />}
          <Button size="sm" onClick={() => inputRef.current?.click()} disabled={uploading} className="h-9 rounded-full bg-[#FF5F15] hover:bg-[#E65100] text-white" data-testid="job-photo-upload-btn">
            <Camera className="w-4 h-4 mr-1.5" />{uploading ? "Uploading…" : "Add photos"}
          </Button>
        </div>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={onPick} data-testid="job-photo-file-input" />
      </div>

      {photos.length === 0 && uploads.length === 0 ? (
        <button type="button" onClick={() => inputRef.current?.click()} className="w-full py-12 rounded-xl border-2 border-dashed border-border text-sm text-muted-foreground hover:border-[#FF5F15] hover:text-foreground transition-colors" data-testid="job-photos-empty">
          No photos yet. Tap to add some, or drag them here.
        </button>
      ) : (
        <>
          {photos.length > 0 && <div className="mb-3"><PhotoFilters photos={photos} categories={categories} category={filterCat} setCategory={setFilterCat} uploader={uploader} setUploader={setUploader} /></div>}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2" data-testid="job-photos-grid">
            {uploads.map((u) => <UploadingThumb key={u.key} up={u} className="rounded-lg" />)}
            {shown.map((p, i) => (
              <PhotoTile key={p.id} photo={p} categories={categories} selecting={selecting} selected={selected.has(p.id)} canDelete={canEdit(p)}
                onOpen={() => setViewing(i)} onToggle={() => toggle(p.id)} onDelete={() => remove(p)} />
            ))}
          </div>
          {shown.length === 0 && photos.length > 0 && <p className="py-8 text-center text-sm text-muted-foreground" data-testid="job-photos-no-match">No photos match these filters.</p>}
        </>
      )}
      {selecting && <SelectionBar count={selected.size} total={shown.length} busy={busy} categories={categories} onMove={bulkMove} onClear={endSelect}
        onAll={() => setSelected(new Set(shown.map((p) => p.id)))} onDownload={bulkDownload} onDelete={bulkDelete} />}
      <PhotoViewer photos={shown} categories={categories} index={viewing} onIndex={setViewing} onClose={() => setViewing(null)} canEdit={canEdit} onSave={save} onDelete={remove} />
    </section>
  );
}
